import encryptDecrypt from '../../cryptography/encryptDecrypt';
import config from '../../../config/config';
import moment from 'moment';
import bulkExpressionParser from '../simpleScript/expressionParser/bulkExpressionParser';
import sizeof from 'object-sizeof';
import { v4 as uuidv4 } from 'uuid';

export default async function(
    functionObj={},
    variableObject={},
    parameterObject={},
    functionMap={},
    variableIdMap={}
){
    let payload;
    try{
        const {
            executionObject={}
        } = functionObj;
        const {
            variableName='',
            variableUnavailable=false,
        } = executionObject;
        const {
            companyId='', user={}
        } = parameterObject;

        if(!variableName || variableUnavailable){
            return {
                updatedVariableObject : variableObject,
                hasError : true,
                errorPayload : 'Variable name is missing.',
                hasReturnStatement : false,
                returnStatement : undefined
            }
        }

        let parsedParameters = await bulkExpressionParser({
            executionObject,
            variableObject,
            parameterObject,
            functionMap,
            variableIdMap,
            useAllFields : true
        });

        if(parsedParameters.error){
            variableObject[variableName] = {
                error : {
                    message : 'Failed to parse function parameters.'
                }
            }
            return {
                updatedVariableObject : variableObject,
                hasError : true,
                errorPayload : 'Failed to resolve function parameters.',
                hasReturnStatement : false,
                returnStatement : undefined
            }
        }

        let variableCopy = parsedParameters['variableObject'];
        let resolvedParams = parsedParameters['accumulator'];

        if(!resolvedParams['base64']){
            variableObject[variableName] = {
                error: {
                    message: 'File base64 text is missing.'
                }
            };
            return {
                updatedVariableObject: variableObject,
                hasError: true,
                errorPayload: 'File base64 text is missing.',
                hasReturnStatement: false,
                returnStatement: undefined
            };
        }

        let chunkSize = sizeof(resolvedParams['base64']);
        if(chunkSize > 10485760){
            variableObject[variableName] = {
                error: {
                    message: 'Chunk size exceeds 10MB limit.'
                }
            };
            return {
                updatedVariableObject: variableObject,
                hasError: true,
                errorPayload: 'Chunk size exceeds 10MB limit.',
                hasReturnStatement: false,
                returnStatement: undefined
            };
        }
        
        payload = {
            companyId,
            fileName : resolvedParams['fileName'] || '',
            fileKey : resolvedParams['fileKey'] || '',
            fileType : resolvedParams['fileType'] || '',
            projectId : resolvedParams['projectId'] || '',
            fileStorageServiceId : resolvedParams['fileStorageServiceId'] || '',
            fileOrigin : 'inhouz function',
            user,
            queryKey : uuidv4(),
            uploadId : resolvedParams['uploadId'] || '',
            chunkOffset : resolvedParams['chunkOffset'] || 0,
            fileSize : resolvedParams['totalFileSize'] || 0,
            totalFileSize : resolvedParams['totalFileSize'] || 0,
            blockId : resolvedParams['blockIndex'] || 0,
            totalBlockCount : resolvedParams['totalBlockCount'] || 0,
            sessionUri : resolvedParams['sessionUri'] || '',
            isUploadStream: true,
            isFirstChunk : resolvedParams['blockIndex'] === 0,
            isLastChunk : (resolvedParams['blockIndex'] === (resolvedParams['totalBlockCount'] - 1))
        }

        if(payload.isLastChunk){
            payload['parts'] = resolvedParams['uploadedParts'] || [];
        }

        if(!payload['fileStorageServiceId']){
            variableObject[variableName] = {
                error: {
                    message: 'File Storage Service ID is missing.'
                }
            };
            return {
                updatedVariableObject: variableObject,
                hasError: true,
                errorPayload: 'File Storage Service ID is missing.',
                hasReturnStatement: false,
                returnStatement: undefined
            };
        }

        if(!payload['fileKey']){
            variableObject[variableName] = {
                error: {
                    message: 'File Key is missing.'
                }
            };
            return {
                updatedVariableObject: variableObject,
                hasError: true,
                errorPayload: 'File Key is missing.',
                hasReturnStatement: false,
                returnStatement: undefined
            };
        }

        let encryptedPayload = encryptDecrypt(
            JSON.stringify({
                payload: payload,
                expirationTimestamp: moment().add(1000, 'seconds').unix() * 1000
            }),
            true
        );

        let response = await fetch(
            `${config.devServerProxy}/api/multimedia/upload`,
            {
                method: 'POST',
                headers: {
                    'Accept': "application/json",
                    'Content-Type': "application/json",
                    'Cache': 'no-cache'
                },
                credentials: 'omit',
                body: JSON.stringify({
                    encryptedPayload,
                    base64 : resolvedParams['base64']
                })
            }
        )
        .then(resp => resp.json())
        .catch((error) => {
            console.error(`[Client] Fetch error for chunk ${resolvedParams['blockIndex'] + 1}:`, error.message);
            return { error: { message: error.message } };
        });

        let uploadedParts = Array.isArray(resolvedParams['uploadedParts']) ? 
        [...resolvedParams['uploadedParts']] : [];
        if(response.eTag){
            uploadedParts.push({
                PartNumber: resolvedParams['blockIndex'] + 1,
                ETag: response.eTag
            });
        }

        variableCopy[variableName] = {
            ...response,
            uploadedParts
        }

        return {
            hasError : false,
            errorPayload : undefined,
            hasReturnStatement : false,
            returnStatement : undefined,
            updatedVariableObject : variableCopy
        };
    }catch(e){
        console.error('/utils/functionParser/general/functionTypeParser/fileStorageStreamUploadParser catch block error', e);
        
        if(payload && payload.fileKey && payload.companyId){
            console.log(`[Client] Attempting to delete incomplete file ${payload.fileName} due to upload error.`);
            let encryptedPayload = encryptDecrypt(
                JSON.stringify({
                    payload : {
                        ...payload,
                        fileKeyList : [{
                            fileKey : payload['fileKey'],
                            companyId : payload['companyId']
                        }]
                    },
                    expirationTimestamp : moment().add(1000, 'seconds').unix() * 1000
                }),
                true
            );

            fetch(
                `${config.devServerProxy}/api/multimedia/deletefiles`,
                {
                    method : 'POST',
                    headers : {
                        'Accept':"application/json",
                        'Content-Type':"application/json",
                        'Cache': 'no-cache'
                    },
                    credentials : 'omit',
                    body : JSON.stringify({
                        encryptedPayload
                    })
                }
            )
            .then(resp => resp.json())
            .then(deleteResponse => {
                console.log(`[Client] Delete attempt response for incomplete file ${payload.fileName}:`, deleteResponse);
            })
            .catch((error) => {
                console.error(`[Client] Error attempting to delete incomplete file ${payload.fileName}:`, error.message);
            });
        }

        return {
            updatedVariableObject : variableObject,
            hasError : true,
            errorPayload : e.stack,
            hasReturnStatement : false,
            returnStatement : undefined
        }
    }
}